import {Dates} from '@smartmixin/core';
import React, {PropsWithChildren} from 'react';
import {IntlProvider, useIntl} from 'react-intl';
import {useLocation} from 'react-router';
import {parseHelpLocaleFromPath} from '../help-center/locales';
import {loadMessages, onError} from './LanguageProvider';

/**
 * URL-locale override for the locale-prefixed help URLs (#70).
 *
 * On `/help/fr/*` (and de/it/es) the whole page — chrome, category labels,
 * Helmet's `<html lang>`, og:locale — must follow the URL's locale rather than
 * the visitor's UI language, so the prerenderer and crawlers (which always run
 * with the default language) capture correctly-localized pages. This nests a
 * second IntlProvider under the app-wide LanguageProvider whenever the URL
 * carries a help locale prefix that differs from the active locale.
 *
 * Must be mounted inside the router (it reads useLocation) — it wraps
 * AppContainer's content. While the translation chunk loads, children render
 * under the outer locale; the prerenderer waits for `<html lang>` to match the
 * route locale, so it never captures the pre-switch frame.
 */
const HelpUrlLocaleProvider = ({children}: PropsWithChildren) => {
  const {pathname} = useLocation();
  const urlLocale = parseHelpLocaleFromPath(pathname);
  const outerLocale = useIntl().locale;

  const [messagesByLocale, setMessagesByLocale] = React.useState<
    Record<string, Record<string, string>>
  >({});

  React.useEffect(() => {
    if (!urlLocale || messagesByLocale[urlLocale]) {
      return;
    }
    let cancelled = false;
    loadMessages(urlLocale).then((messages) => {
      if (!cancelled) {
        setMessagesByLocale((prev) => ({...prev, [urlLocale]: messages}));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [urlLocale, messagesByLocale]);

  // Dates.setLocale is module-global (LanguageProvider sets it to the user
  // language during render) — mirror the override while on a locale-prefixed
  // help URL and restore it on the way out.
  const active = urlLocale && urlLocale !== outerLocale ? urlLocale : null;
  React.useEffect(() => {
    if (!active) {
      return;
    }
    Dates.setLocale(active);
    return () => {
      Dates.setLocale(outerLocale);
    };
  }, [active, outerLocale]);

  const messages = active ? messagesByLocale[active] : undefined;
  if (!active || !messages) {
    return <>{children}</>;
  }

  return (
    <IntlProvider locale={active} messages={messages} onError={onError}>
      {children}
    </IntlProvider>
  );
};

export default HelpUrlLocaleProvider;
